// src/components/WarehouseDetail.js
// Warehouse detail — /warehouses-instructions/:warehouseId.
// Header (back + name + actions) over a two-card grid: Configuration
// (contacts + the instructions finally readable as a document) | Clients
// associés (count + chips linking to /clients-instructions/:id).
// Edit happens in place with the same sectioned form as the create modal.
// The API has no single-GET endpoint — the enriched list response is the
// source, picked by id.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft, FaTrash, FaPen, FaWarehouse, FaCheckCircle, FaTimesCircle,
  FaSpinner, FaExclamationTriangle
} from 'react-icons/fa';
import {
  getWarehouseEmails, EmailListEditor, ClientPicker,
  validateWarehouseForm, warehousePayload
} from './WarehousesInstructions';
import './WarehouseDetail.css';

const WarehouseDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { warehouseId } = useParams();

  const [warehouse, setWarehouse] = useState(undefined); // undefined = loading, null = not found
  const [clients, setClients] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const feedbackTimer = useRef(null);

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const showFeedback = useCallback((type, text) => {
    clearTimeout(feedbackTimer.current);
    setFeedback({ type, text });
    feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
  }, []);

  useEffect(() => () => clearTimeout(feedbackTimer.current), []);

  const fetchWarehouse = useCallback(async () => {
    try {
      const res = await axios.get('/api/logistics/warehouses-edn', { headers: authHeaders() });
      const found = (res.data || []).find((w) => String(w.id) === String(warehouseId));
      setWarehouse(found || null);
    } catch (error) {
      console.error('Failed to fetch warehouse:', error);
      setWarehouse(null);
    }
  }, [warehouseId]);

  useEffect(() => {
    fetchWarehouse();
    axios.get('/api/logistics/clients-edn', { headers: authHeaders() })
      .then((res) => setClients(res.data))
      .catch((error) => console.error('Failed to fetch clients:', error));
  }, [fetchWarehouse]);

  const startEditing = () => {
    setForm({
      name: warehouse.name || '',
      emails: getWarehouseEmails(warehouse).length > 0 ? getWarehouseEmails(warehouse) : [''],
      instructions: warehouse.instructions || '',
      clientIds: (warehouse.clients || []).map((c) => c.id)
    });
    setFormError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setForm(null);
    setFormError(null);
  };

  const handleSave = async () => {
    const error = validateWarehouseForm(form, t);
    if (error) { setFormError(error); return; }
    setSaving(true);
    setFormError(null);
    try {
      await axios.put(`/api/logistics/warehouses-edn/${warehouseId}`, warehousePayload(form), { headers: authHeaders() });
      setIsEditing(false);
      setForm(null);
      showFeedback('success', t('warehousesInstructions.updateSuccess', 'Entrepôt mis à jour.'));
      fetchWarehouse();
    } catch (error) {
      console.error('Failed to update warehouse:', error);
      setFormError(error.response?.data?.detail || t('warehousesInstructions.updateError', 'Échec de la mise à jour de l’entrepôt.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      t('warehousesInstructions.confirmDelete', 'Supprimer cet entrepôt ? Les clients associés ne seront pas supprimés.')
    );
    if (!confirmed) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/logistics/warehouses-edn/${warehouseId}`, { headers: authHeaders() });
      navigate('/warehouses-instructions');
    } catch (error) {
      console.error('Failed to delete warehouse:', error);
      setDeleting(false);
      showFeedback('error', t('warehousesInstructions.deleteError', 'Échec de la suppression de l’entrepôt.'));
    }
  };

  const toggleFormClient = (clientId) => {
    setForm((prev) => ({
      ...prev,
      clientIds: prev.clientIds.includes(clientId)
        ? prev.clientIds.filter((id) => id !== clientId)
        : [...prev.clientIds, clientId]
    }));
  };

  if (warehouse === undefined) {
    return (
      <div className="warehouse-detail">
        <div className="whd-skeleton" aria-hidden="true">
          <div className="whd-skeleton-row whd-skeleton-header" />
          {Array.from({ length: 4 }, (_, i) => <div key={i} className="whd-skeleton-row" />)}
        </div>
      </div>
    );
  }

  if (warehouse === null) {
    return (
      <div className="warehouse-detail">
        <div className="whd-notfound">
          <FaExclamationTriangle className="whd-notfound-icon" />
          <p>{t('warehousesInstructions.notFound', 'Entrepôt introuvable.')}</p>
          <button type="button" className="u-btn u-btn--secondary" onClick={() => navigate('/warehouses-instructions')}>
            <FaArrowLeft /> {t('warehousesInstructions.backToList', 'Retour aux entrepôts')}
          </button>
        </div>
      </div>
    );
  }

  const emails = getWarehouseEmails(warehouse);
  const clientList = warehouse.clients || [];

  return (
    <div className="warehouse-detail">
      {/* Header */}
      <div className="whd-header">
        <div className="whd-header-left">
          <button
            type="button"
            className="whd-back-button"
            onClick={() => navigate('/warehouses-instructions')}
            aria-label={t('warehousesInstructions.backToList', 'Retour aux entrepôts')}
          >
            <FaArrowLeft />
          </button>
          <span className="whd-avatar"><FaWarehouse /></span>
          <div className="whd-identity">
            <h1>{warehouse.name}</h1>
            <span className="whd-identity-meta">
              {t('warehousesInstructions.identityMeta', '{{emails}} emails · {{clients}} clients', {
                emails: emails.length, clients: clientList.length
              })}
            </span>
          </div>
        </div>
        <div className="whd-header-actions">
          {isEditing ? (
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={cancelEditing} disabled={saving}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="button" className="u-btn u-btn--primary" onClick={handleSave} disabled={saving}>
                {saving
                  ? (<><FaSpinner className="whd-spin" /> {t('warehousesInstructions.saving', 'Enregistrement…')}</>)
                  : t('common.save', 'Enregistrer')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="whd-delete-btn"
                onClick={handleDelete}
                disabled={deleting}
                title={t('common.delete', 'Supprimer')}
              >
                {deleting ? <FaSpinner className="whd-spin" /> : <FaTrash />}
              </button>
              <button type="button" className="u-btn u-btn--primary" onClick={startEditing}>
                <FaPen /> {t('warehousesInstructions.edit', 'Modifier')}
              </button>
            </>
          )}
        </div>
      </div>

      {feedback && (
        <div className={`whd-feedback ${feedback.type}`} role="status">
          {feedback.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
          {feedback.text}
        </div>
      )}

      {isEditing ? (
        /* -------- Edit: sectioned form, one card -------- */
        <div className="whd-card whd-edit-card">
          <div className="whd-form-section">
            <h4>{t('warehousesInstructions.sectionIdentity', 'Identité')}</h4>
            <div className="whd-field">
              <label htmlFor="whd-name">{t('warehousesInstructions.name', 'Nom')} *</label>
              <input
                id="whd-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
          </div>
          <div className="whd-form-section">
            <h4>{t('warehousesInstructions.sectionContact', 'Contact')}</h4>
            <p className="whd-section-hint">
              {t('warehousesInstructions.sectionContactHint', 'Adresses utilisées par l’IA pour contacter l’entrepôt.')}
            </p>
            <EmailListEditor
              emails={form.emails}
              onChange={(nextEmails) => setForm((f) => ({ ...f, emails: nextEmails }))}
              t={t}
            />
          </div>
          <div className="whd-form-section">
            <h4>{t('warehousesInstructions.sectionInstructions', 'Instructions IA')}</h4>
            <p className="whd-section-hint">
              {t('warehousesInstructions.sectionInstructionsHint', 'Consignes suivies par l’IA lors des échanges avec cet entrepôt.')}
            </p>
            <textarea
              rows={10}
              value={form.instructions}
              onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))}
              placeholder={t('warehousesInstructions.instructionsPlaceholder', 'Ex. : Toujours fournir la référence de commande…')}
            />
          </div>
          <div className="whd-form-section">
            <h4>{t('warehousesInstructions.sectionClients', 'Clients associés')}</h4>
            <ClientPicker
              clients={clients}
              selectedIds={form.clientIds}
              onToggle={toggleFormClient}
              t={t}
            />
          </div>
          {formError && <div className="whd-form-error">{formError}</div>}
        </div>
      ) : (
        /* -------- View: Configuration | Clients -------- */
        <div className="whd-grid">
          <div className="whd-card">
            <div className="whd-card-title">
              <h2>{t('warehousesInstructions.configTitle', 'Configuration')}</h2>
            </div>
            <div className="whd-def-row">
              <span className="whd-def-label">{t('warehousesInstructions.contact', 'Contact')}</span>
              {emails.length === 0 ? (
                <span className="whd-flag-warn">
                  <FaExclamationTriangle /> {t('warehousesInstructions.noEmailHint', 'Aucun email — l’IA ne peut pas contacter cet entrepôt.')}
                </span>
              ) : (
                <ul className="whd-email-list">
                  {emails.map((email) => (
                    <li key={email}><a href={`mailto:${email}`}>{email}</a></li>
                  ))}
                </ul>
              )}
            </div>
            <div className="whd-def-row">
              <span className="whd-def-label">{t('warehousesInstructions.sectionInstructions', 'Instructions IA')}</span>
              {warehouse.instructions ? (
                <div className="whd-instructions-doc">{warehouse.instructions}</div>
              ) : (
                <span className="whd-flag-warn">
                  <FaExclamationTriangle /> {t('warehousesInstructions.instructionsMissingHint', 'À compléter — l’IA contactera cet entrepôt sans consigne particulière.')}
                </span>
              )}
            </div>
          </div>

          <div className="whd-card">
            <div className="whd-card-title">
              <h2>{t('warehousesInstructions.sectionClients', 'Clients associés')}</h2>
              <span className="whd-card-title-meta">{clientList.length}</span>
            </div>
            {clientList.length === 0 ? (
              <p className="whd-muted-text">
                {t('warehousesInstructions.noClientsAssociated', 'Aucun client associé à cet entrepôt.')}
              </p>
            ) : (
              <div className="whd-client-chips">
                {[...clientList]
                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' }))
                  .map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      className="whd-client-chip"
                      title={client.name}
                      onClick={() => navigate(`/clients-instructions/${client.id}`)}
                    >
                      {client.name}
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseDetail;
